import React, { useState } from 'react';
import { LineChart, PieChart, FilterX, ArrowRight } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент графиков инвестиционного портфеля
 */
const PortfolioChart = ({ 
  portfolioHistory, 
  assetAllocationChart, 
  results, 
  formData,
  assetTypes
}) => {
  const [activeTab, setActiveTab] = useState('growth');
  const [timeframe, setTimeframe] = useState('all'); // 'year', 'all'

  if (!portfolioHistory || portfolioHistory.length === 0 || !results) {
    return null;
  }

  // Подготовка данных для графика роста портфеля
  const prepareGrowthData = () => {
    // Фильтруем историю в зависимости от выбранного периода
    let filteredHistory = [...portfolioHistory];
    if (timeframe === 'year' && portfolioHistory.length > 12) {
      filteredHistory = portfolioHistory.slice(0, 12);
    }

    // Создаем точки графика
    const chartData = filteredHistory.map((item, index) => ({
      month: item.month,
      label: `${Math.floor(index / 12) + 1}г ${(index % 12) + 1}м`,
      balance: Math.round(item.endBalance),
      invested: Math.round(
        results.initialInvestment + 
        (item.month * parseFloat(formData.monthlyContribution)) +
        (formData.includeCashFlow ? 
          portfolioHistory
            .slice(0, index + 1)
            .reduce((sum, h) => sum + h.additionalInvestments - h.withdrawals, 0) : 0
        )
      )
    }));

    return chartData;
  };

  const growthData = prepareGrowthData();

  // Рассчитываем максимальные значения для графика
  const maxBalance = Math.max(...growthData.map(d => d.balance));
  const yAxisMax = Math.ceil(maxBalance * 1.1 / 100000) * 100000; // Округляем вверх до ближайших 100к

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <LineChart className="w-5 h-5 mr-2 text-indigo-500" />
          Динамика инвестиционного портфеля
        </h3>

        {/* Селектор периода */}
        {portfolioHistory.length > 12 && (
          <div className="flex space-x-2">
            <button
              onClick={() => setTimeframe('year')}
              className={`px-3 py-1 text-sm rounded-md ${
                timeframe === 'year'
                  ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              Первый год
            </button>
            <button
              onClick={() => setTimeframe('all')}
              className={`px-3 py-1 text-sm rounded-md ${
                timeframe === 'all'
                  ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              Весь период
            </button>
          </div>
        )}
      </div>

      {/* Вкладки для разных типов графиков */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('growth')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'growth'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Рост портфеля
        </button>
        <button
          onClick={() => setActiveTab('allocation')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'allocation'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Структура портфеля
        </button>
      </div>

      {/* Содержимое графиков */}
      <div className="relative">
        {/* График роста портфеля */}
        {activeTab === 'growth' && (
          <div>
            <div className="flex mb-4">
              <div className="flex items-center mr-4">
                <div className="w-3 h-3 rounded-full bg-indigo-500 mr-2"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">Стоимость портфеля</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-gray-300 dark:bg-gray-600 mr-2"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">Вложенные средства</span>
              </div>
            </div>

            {/* SVG график роста портфеля */}
            <div className="relative h-80 mt-2">
              <svg className="w-full h-full" viewBox="0 0 1000 400">
                {/* Ось Y и горизонтальные линии */}
                {Array.from({ length: 6 }).map((_, i) => {
                  const y = 350 - (i * 350 / 5);
                  const value = (i * yAxisMax / 5);
                  return (
                    <g key={`y-axis-${i}`}>
                      <line 
                        x1="60" 
                        y1={y} 
                        x2="950" 
                        y2={y} 
                        stroke="currentColor" 
                        strokeOpacity="0.1" 
                        strokeWidth="1"
                      />
                      <text 
                        x="50" 
                        y={y + 5} 
                        textAnchor="end" 
                        fontSize="12" 
                        fill="currentColor" 
                        fillOpacity="0.6"
                      >
                        {formatCurrency(value, 0)}
                      </text>
                    </g>
                  );
                })}

                {/* Ось X и вертикальные линии */}
                {growthData.filter((_, i) => i % Math.max(1, Math.floor(growthData.length / 10)) === 0).map((point, i, filtered) => {
                  const x = 60 + ((i) * (890 / (filtered.length - 1 || 1)));
                  return (
                    <g key={`x-axis-${i}`}>
                      <line 
                        x1={x} 
                        y1="50" 
                        x2={x} 
                        y2="350" 
                        stroke="currentColor" 
                        strokeOpacity="0.1" 
                        strokeWidth="1"
                      />
                      <text 
                        x={x} 
                        y="370" 
                        textAnchor="middle" 
                        fontSize="12" 
                        fill="currentColor" 
                        fillOpacity="0.6"
                      >
                        {point.label}
                      </text>
                    </g>
                  );
                })}

                {/* Линия вложенных средств */}
                <path
                  d={`
                    M ${60} ${350 - (growthData[0].invested / yAxisMax * 350)}
                    ${growthData.map((point, i) => {
                      const x = 60 + (i * (890 / (growthData.length - 1)));
                      const y = 350 - (point.invested / yAxisMax * 350);
                      return `L ${x} ${y}`;
                    }).join(' ')}
                  `}
                  fill="none"
                  stroke="#9CA3AF"  // gray-400
                  strokeWidth="2"
                  strokeDasharray="5,5"
                />

                {/* Линия стоимости портфеля */}
                <path
                  d={`
                    M ${60} ${350 - (growthData[0].balance / yAxisMax * 350)}
                    ${growthData.map((point, i) => {
                      const x = 60 + (i * (890 / (growthData.length - 1)));
                      const y = 350 - (point.balance / yAxisMax * 350);
                      return `L ${x} ${y}`;
                    }).join(' ')}
                  `}
                  fill="none"
                  stroke="#4F46E5"  // indigo-600
                  strokeWidth="3"
                />

                {/* Область под линией стоимости портфеля */}
                <path
                  d={`
                    M ${60} ${350}
                    ${growthData.map((point, i) => {
                      const x = 60 + (i * (890 / (growthData.length - 1)));
                      const y = 350 - (point.balance / yAxisMax * 350);
                      return `L ${x} ${y}`;
                    }).join(' ')}
                    L ${60 + (890)} ${350}
                    Z
                  `}
                  fill="url(#gradient)"
                  fillOpacity="0.2"
                />

                {/* Градиент для заливки */}
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#4F46E5" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#4F46E5" stopOpacity="0.1" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            {/* Сводка */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <h4 className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1">Начальная инвестиция</h4>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatCurrency(results.initialInvestment, 0)}</p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <h4 className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1">Всего вложено</h4>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatCurrency(results.totalInvested - results.totalWithdrawals, 0)}</p>
              </div>
              <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800">
                <h4 className="text-xs text-indigo-500 dark:text-indigo-400 font-medium mb-1">Доход от инвестиций</h4>
                <p className="text-lg font-semibold text-indigo-700 dark:text-indigo-300">{formatCurrency(results.absoluteReturn, 0)}</p>
              </div>
            </div>
          </div>
        )}

        {/* График структуры портфеля */}
        {activeTab === 'allocation' && assetAllocationChart.length > 0 && (
          <div>
            <div className="flex flex-col md:flex-row gap-8">
              {/* Круговая диаграмма */}
              <div className="md:w-1/2">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 text-center">
                  Распределение активов
                </h4>
                <div className="relative h-64 md:h-72">
                  <svg className="w-full h-full" viewBox="0 0 400 400">
                    {/* Пирог */}
                    {assetAllocationChart.map((asset, i, arr) => {
                      let startAngle = 0;
                      for (let j = 0; j < i; j++) {
                        startAngle += (arr[j].percentage / 100) * 360;
                      }
                      const endAngle = startAngle + (asset.percentage / 100) * 360;
                      
                      // Преобразование углов в радианы и координаты
                      const startRad = (startAngle - 90) * Math.PI / 180;
                      const endRad = (endAngle - 90) * Math.PI / 180;
                      
                      const centerX = 200;
                      const centerY = 200;
                      const radius = 150;
                      
                      const x1 = centerX + radius * Math.cos(startRad);
                      const y1 = centerY + radius * Math.sin(startRad);
                      const x2 = centerX + radius * Math.cos(endRad);
                      const y2 = centerY + radius * Math.sin(endRad);
                      
                      // Проверка, больше ли сегмент половины круга
                      const largeArcFlag = (endAngle - startAngle) > 180 ? 1 : 0;
                      
                      // Путь для дуги
                      const path = `
                        M ${centerX} ${centerY}
                        L ${x1} ${y1}
                        A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}
                        Z
                      `;
                      
                      return (
                        <path 
                          key={asset.id} 
                          d={path} 
                          fill={asset.color}
                          stroke="#fff"
                          strokeWidth="2"
                        />
                      );
                    })}
                    
                    {/* Текст в центре */}
                    <text 
                      x="200" 
                      y="180" 
                      textAnchor="middle" 
                      fontSize="18" 
                      fontWeight="bold" 
                      fill="currentColor"
                    >
                      Итого
                    </text>
                    <text 
                      x="200" 
                      y="210" 
                      textAnchor="middle" 
                      fontSize="16" 
                      fill="currentColor"
                    >
                      {formatCurrency(results.finalPortfolioValue, 0)}
                    </text>
                  </svg>
                </div>
              </div>
              
              {/* Список активов */}
              <div className="md:w-1/2">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Детализация портфеля
                </h4>
                <div className="space-y-3">
                  {assetAllocationChart.map(asset => (
                    <div key={asset.id} className="flex items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="w-4 h-4 rounded-full mr-3" style={{ backgroundColor: asset.color }}></div>
                      <div className="flex-grow">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{asset.name}</span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(asset.value, 0)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div 
                              className="h-2 rounded-full" 
                              style={{ 
                                width: `${asset.percentage}%`,
                                backgroundColor: asset.color
                              }}
                            ></div>
                          </div>
                          <span className="ml-2 text-xs font-medium text-gray-500 dark:text-gray-400 min-w-[40px] text-right">
                            {formatNumber(asset.percentage, 1)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Ожидаемая доходность по типам активов */}
                <div className="mt-6 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800">
                  <h4 className="text-sm font-medium text-indigo-700 dark:text-indigo-300 mb-2 flex items-center">
                    <TrendingUp className="w-4 h-4 mr-1" />
                    Ожидаемая годовая доходность
                  </h4>
                  <div className="text-xs text-indigo-600 dark:text-indigo-400 space-y-1">
                    {assetTypes.map(type => (
                      formData.assetAllocation[type.id] > 0 ? (
                        <div key={type.id} className="flex justify-between">
                          <span>{type.name}:</span>
                          <span className="font-medium">{formData.expectedReturns[type.id]}%</span>
                        </div>
                      ) : null
                    ))}
                    <div className="border-t border-indigo-200 dark:border-indigo-700 pt-1 mt-1">
                      <div className="flex justify-between text-sm font-medium text-indigo-700 dark:text-indigo-300">
                        <span>Средневзвешенная:</span>
                        <span>{formatNumber(results.annualizedReturn, 2)}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Компонент иконки TrendingUp (аналог Lucide иконки)
const TrendingUp = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
    <polyline points="17 6 23 6 23 12"></polyline>
  </svg>
);

export default PortfolioChart;